import { FilterBy, PageQuery } from "../../../utils/models/pageQuery";
import { createSearchParams, useLocation, useSearchParams } from "react-router-dom";
import { getInitialQuery } from "../functions/getInitialQuery.function";
import { useContext, useEffect, useState } from "react";
import {
  buildQueryParameters,
  MANAGED_QUERY_KEYS,
} from "../functions/setQueryParameters.function";
import { enviroment } from "../../../enviroment";
import { ProfileContext } from "../../../contexts/ProfileContext";
import {
  DEFAULT_PAGE_SIZES,
  listKeyFromPathname,
  readRememberedPageSize,
  rememberPageSize,
} from "../../../utils/pageSizeMemory";
import SortByFieldEvent from "../../components/GenericTable/Models/sortByFieldEvent";
import { SearchQueryEvent } from "../../components/GenericTable/Models/searchQueryEvent";
import { PageSizeEvent } from "../../components/GenericTable/SubComponents/TableFooter/PageSizeSelector";
import { PageChangeEvent } from "../../components/GenericTable/Models/pageChangeEvent";
import { FilterChangeEvent } from "../../components/GenericTable/SubComponents/TableHeader/ListHeader";

// Page-size memory (flag rememberPageSizeEnabled, see utils/pageSizeMemory):
// listKey = the storage key for this list (default: the route path, so one
// key per screen no matter the filters); pageSizes = the sizes the screen's
// footer offers (default: the footer's own [12, 25, 50, 100]) - a remembered
// value outside it is ignored, never shown.
export interface PageQueryOptions {
  listKey?: string;
  pageSizes?: readonly number[];
}

export function usePageQuery(
  defaultPageSize?: number,
  options?: PageQueryOptions
): [query: PageQuery, setQuery: QueryChanger] {
  const [queryParams, setQParams] = useSearchParams();
  const location = useLocation();
  // Flag rememberPageSizeEnabled: the per-user key needs the profile's b2CId.
  // Off, the context is not even subscribed to (today's render behaviour);
  // the flag is a build-time constant, so the hook order is stable within a
  // session (same precedent as ProfileProvider's useNavigate).
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const profile = enviroment.rememberPageSizeEnabled ? useContext(ProfileContext) : undefined;
  const b2CId = profile?.b2CId;
  const listKey = options?.listKey ?? listKeyFromPathname(location.pathname);
  const allowedPageSizes = options?.pageSizes ?? DEFAULT_PAGE_SIZES;
  // The default the URL falls back to when it names no pageSize: the user's
  // remembered size for this list with the flag on, else the caller's
  // default (getInitialQuery's own 12 when that is undefined). Read per
  // render so the URL->state sync below and the normalization classification
  // always agree with what the footer last stored; off = defaultPageSize,
  // no storage access.
  const effectiveDefaultPageSize = enviroment.rememberPageSizeEnabled
    ? readRememberedPageSize(b2CId, listKey, allowedPageSizes) ?? defaultPageSize
    : defaultPageSize;
  const initialQuery = getInitialQuery(queryParams, effectiveDefaultPageSize);
  const [currentPageQuery, setPageQuery] = useState<PageQuery>(initialQuery);

  // State→URL sync with three outcomes, decided per write (a mount-time ref
  // cannot tell them apart: Back/Forward, nav-clicks to the bare route and
  // StrictMode's double effect run all arrive after "first sync"):
  //
  // 1. SKIP when the URL already says exactly this - a state update that came
  //    FROM the URL (Back/Forward, deep link) must not navigate again; the
  //    echo used to push a duplicate entry, killing the forward stack and
  //    making Back need two presses.
  // 2. REPLACE when the write is pure normalization - it only adds defaults
  //    the URL lacked (e.g. "/logo" → "/logo?pageSize=12&pageIndex=1") and
  //    changes nothing the user set. Rewriting in place carries
  //    location.state along, so navigate("/logo", { state }) consumers keep
  //    their state.
  // 3. PUSH for real user actions (paging/filter/search change an existing
  //    value or introduce a non-default one), so Back steps through those
  //    exactly as before.
  //
  // Params outside MANAGED_QUERY_KEYS are copied over untouched - a shared
  // deep link with extra params keeps them through normalization.
  useEffect(() => {
    const target = createSearchParams(buildQueryParameters(currentPageQuery));
    queryParams.forEach((value, key) => {
      if (!(MANAGED_QUERY_KEYS as readonly string[]).includes(key)) {
        target.append(key, value);
      }
    });

    const canonical = (p: URLSearchParams) =>
      Array.from(p.entries())
        .sort(([ak, av], [bk, bv]) =>
          ak === bk ? av.localeCompare(bv) : ak.localeCompare(bk)
        )
        .map(([k, v]) => `${k}=${v}`)
        .join("&");
    if (canonical(target) === canonical(queryParams)) return;

    const defaults = createSearchParams(
      buildQueryParameters(
        getInitialQuery(new URLSearchParams(), effectiveDefaultPageSize)
      )
    );
    let isNormalization = true;
    for (const key of MANAGED_QUERY_KEYS) {
      const current = queryParams.getAll(key);
      const next = target.getAll(key);
      if (current.length > 0) {
        // present in the URL: any change (or removal) is a user action
        if (JSON.stringify(current) !== JSON.stringify(next)) {
          isNormalization = false;
          break;
        }
      } else if (next.length > 0) {
        // newly added: only default values count as normalization
        if (JSON.stringify(next) !== JSON.stringify(defaults.getAll(key))) {
          isNormalization = false;
          break;
        }
      }
    }

    setQParams(
      target,
      isNormalization ? { replace: true, state: location.state } : undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageQuery]);

  useEffect(() => {
    const queryFromUrl = getInitialQuery(queryParams, effectiveDefaultPageSize);

    if (JSON.stringify(queryFromUrl) !== JSON.stringify(currentPageQuery)) {
      setPageQuery(queryFromUrl);
    }
  }, [queryParams]);

  // Flag rememberPageSizeEnabled: remember the size the list shows - the
  // footer's choice, a shared link's pageSize and Back/Forward alike (what
  // the user sees is what comes back next time). Only a size the footer
  // offers is stored (the vendor list's hook state can hold the generic 12
  // while the screen shows 24 - that 12 is never written); off = no storage
  // access. allowedPageSizes is left out of the deps on purpose: a caller
  // may pass an inline array, and the write is idempotent anyway.
  const shownPageSize = currentPageQuery.page?.size;
  useEffect(() => {
    if (!enviroment.rememberPageSizeEnabled) return;
    rememberPageSize(b2CId, listKey, shownPageSize, allowedPageSizes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownPageSize, b2CId, listKey]);

  function handleSortByField(event: SortByFieldEvent) {
    setPageQuery((x) => ({
      ...x,
      sortBy: { field: event.value, sortDirection: event.sortDirection },
    }));
  }

  function handleSearchQueryChange(event: SearchQueryEvent) {
    setPageQuery((x) => ({
      ...x,
      searchTerm: event.searchTerm,
      page: { ...x.page, index: 1 },
    }));
  }

  function handlePageSizeChange(event: PageSizeEvent) {
    setPageQuery((x) => ({ ...x, page: { ...x.page, size: event.nextSize } }));
  }

  function handleNewPage(event: PageChangeEvent) {
    setPageQuery((x) => ({ ...x, page: { ...x.page, index: +event.value } }));
  }

  function handleFilterChange(event: FilterChangeEvent) {
    setPageQuery((x) => {
      const newFilters: FilterBy[] = event.activeFilters.map((x) => ({
        field: x.key,
        value: "" + x.value,
      }));

      return { ...x, filterBy: newFilters, page: { ...x.page, index: 1 } };
    });
  }

  const [queryChanger] = useState({
    handleSortByField,
    handleNewPage,
    handlePageSizeChange,
    handleSearchQueryChange,
    handleFilterChange,
    setPageQuery,
  });

  return [currentPageQuery, queryChanger];
}

export interface QueryChanger {
  handleSortByField: (event: SortByFieldEvent) => void;
  handleSearchQueryChange: (event: SearchQueryEvent) => void;
  handlePageSizeChange: (event: PageSizeEvent) => void;
  handleNewPage: (event: PageChangeEvent) => void;
  handleFilterChange: (event: FilterChangeEvent) => void;
  setPageQuery: (query: PageQuery) => void;
}
